import {useCallback} from 'react';
import {useSelector} from 'react-redux';

import type {User} from 'nutshell-core/users/users-types';

import {Stack} from 'shells/layout/stack/stack';
import {LeadAvatar} from 'shells/lead/lead-avatar';
import {LeadValue} from 'shells/lead/value/lead-value';
import {Body} from 'shells/typography/body';

import type {GetLead_lead as Lead} from 'nutshell-graphql-types';

import {MiniActionBar} from '../../core-entity/mini-header/mini-action-bar';
import {MiniHeader} from '../../core-entity/mini-header/mini-header';
import {TAB_CALL_MINI_FIXED_ANCHOR} from '../../core-entity/redux/consts';
import {getCoreEntityPageActionBarTab} from '../../core-entity/redux/core-entity-page-selectors';
import {
    getTabOptions,
    useHandleCallClick,
    useOpenEmailModal,
    useOpenTextModal,
} from '../../core-entity/util/action-bar-helpers';

import {AssigneeButton} from '../deprecated/header/buttons/assignee-button';
import {useUpdateLeadPriority} from '../header/graphql/hooks/use-update-lead-priority';
import {useUpdateLeadWatched} from '../header/graphql/hooks/use-update-lead-watched';
import {HeaderIconToggles} from '../header/header-icon-toggles';

type Props = {
    isShown: boolean;
    contentRef: {
        current: HTMLDivElement | null;
    };
    lead: Lead;
    user: User;
    scrollContainerTop: number;
};

export function LeadHeaderMiniOnScroll(props: Props) {
    const {updateLeadPriority} = useUpdateLeadPriority();
    const {updateLeadWatched} = useUpdateLeadWatched();

    const actionBarTab = useSelector(getCoreEntityPageActionBarTab);

    const openEmailModal = useOpenEmailModal(props.lead);
    const {openTextModal} = useOpenTextModal(props.lead, props.user);
    const {handleCallClick, popoverComponent} = useHandleCallClick(
        TAB_CALL_MINI_FIXED_ANCHOR,
        props.lead.id,
        {
            noPhoneNumbersQuestion: 'No phone numbers associated with this lead',
        }
    );

    const handleHotToggle = useCallback(() => {
        updateLeadPriority({
            input: {id: props.lead.id, priority: props.lead.isHot ? 'NORMAL' : 'HOT'},
            lead: props.lead,
        });
    }, [updateLeadPriority, props.lead]);

    const handleWatchingToggle = useCallback(() => {
        updateLeadWatched({
            input: {id: props.lead.id, shouldWatch: !props.lead.isCurrentUserWatching},
            lead: props.lead,
        });
    }, [updateLeadWatched, props.lead]);

    const options = getTabOptions(openEmailModal, openTextModal, handleCallClick);

    return (
        <MiniHeader
            isShown={props.isShown}
            contentRef={props.contentRef}
            scrollContainerTop={props.scrollContainerTop}
            entityType='lead'
        >
            <Stack spacing={8} isFullWidth={true}>
                <Stack
                    isHorizontal={true}
                    spacing={8}
                    justifyContent='space-between'
                    isFullWidth={true}
                >
                    <div className='flex max-full-width truncate'>
                        <LeadAvatar
                            avatarUrl={props.lead.avatarUrl}
                            leadNumber={props.lead.number}
                        />
                        <Body size='large' color='dark' isBold={true}>
                            {props.lead.name}
                        </Body>
                    </div>
                    <Stack isHorizontal={true} spacing={8} alignItems='start'>
                        <LeadValue
                            valueFormatted={props.lead.value && props.lead.value.formatted}
                        />
                        <div
                            style={{
                                minWidth: 200,
                            }}
                        >
                            <AssigneeButton lead={props.lead} showTooltip={true} />
                        </div>
                        <HeaderIconToggles
                            lead={props.lead}
                            userCanAccessSetup={props.user.permissions.canAccessSetup}
                            onHotToggle={handleHotToggle}
                            onWatchingToggle={handleWatchingToggle}
                        />
                    </Stack>
                </Stack>
                <MiniActionBar
                    entityId={props.lead.id}
                    entityName={'Leads'}
                    entityDisplayName={props.lead.name}
                    options={options}
                    selectedTab={actionBarTab}
                    isShown={props.isShown}
                />
                {popoverComponent}
            </Stack>
        </MiniHeader>
    );
}
